import React, { Suspense, lazy } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { User, LogOut, TrendingUp, Globe, Plus } from 'lucide-react'
// import { DarkModeSwitch } from '@/components/DarkModeSwitch' // Disabled for MVP
const AddSalesModal = lazy(() => import('@/components/orders/AddSalesModal').then(m => ({ default: m.AddSalesModal })))
const AddProductModal = lazy(() => import('@/components/products/AddProductModal').then(m => ({ default: m.AddProductModal })))
import CurrencyQuickPanel from '@/components/currency/CurrencyQuickPanel'
import { ErrorBoundary } from '@/components/ErrorBoundary'

export const TopNavigation: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { user, logout } = useAuth()
  const [addProductOpen, setAddProductOpen] = React.useState(false)
  const [salesOpen, setSalesOpen] = React.useState(false)

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language)
  }

  const handleAddProduct = () => {
    setAddProductOpen(true)
  }

  const handleAddSales = () => {
    setSalesOpen(true)
  }

  // Removed for MVP - incomplete feature
  // const handleSettings = () => {
  //   // TODO: Implement settings functionality
  // }

  return (
    <div className="h-16 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-full items-center justify-between px-6">
        {/* Left side - Action buttons */}
        <div className="flex items-center space-x-3">
          <Button
            onClick={handleAddProduct}
            variant="default"
            size="sm"
            className="flex items-center space-x-2 bg-foreground text-background hover:bg-foreground/90"
          >
            <Plus className="h-4 w-4" />
            <span>{t('products.addProduct')}</span>
          </Button>
          <Button
            onClick={handleAddSales}
            variant="outline"
            size="sm"
            className="flex items-center space-x-2"
          >
            <TrendingUp className="h-4 w-4" />
            <span>{t('orders.addSales')}</span>
          </Button>
        </div>

        {/* Center - Currency Rate Display */}
        <div className="flex-1 flex justify-center">
          <ErrorBoundary
            fallback={
              <div className="text-xs text-muted-foreground">
                {t('common.currencyServiceUnavailable')}
              </div>
            }
          >
            <CurrencyQuickPanel />
          </ErrorBoundary>
        </div>

        {/* Right side - User controls */}
        <div className="flex items-center space-x-4">
          {/* Dark Mode Switch disabled for MVP - text visibility issues */}
          {/* <DarkModeSwitch /> */}

          {/* Language Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center space-x-2">
                <Globe className="h-4 w-4" />
                <Badge variant="secondary" className="text-xs">
                  {i18n.language.toUpperCase()}
                </Badge>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleLanguageChange('en')}>
                English
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleLanguageChange('uk')}>
                Українська
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleLanguageChange('tr')}>
                Türkçe
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Settings button removed for MVP - incomplete feature */}

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center space-x-3 px-3">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary/10 text-primary">
                    <User className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium">
                    {user?.email || 'admin@warehouse.com'}
                  </span>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem>
                <User className="mr-2 h-4 w-4" />
                  <span>{t('profile')}</span>
              </DropdownMenuItem>
              {/* Settings menu item removed for MVP - incomplete feature */}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                <span>{t('logout')}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">{t('common.loading')}</div>}>
        <AddProductModal open={addProductOpen} onOpenChange={setAddProductOpen} />
        <AddSalesModal open={salesOpen} onOpenChange={setSalesOpen} />
      </Suspense>
    </div>
  )
}
